/**
 * Project Issues tab (/projects/:projectId?tab=issues): filtering, sorting and
 * the url shape. Kept free of Angular so the logic is unit-testable.
 *
 * The list API (GET /v1/issue) has no free-text search, so the tab fetches the
 * project's issues once and filters on the client, the way the old
 * /issues/project/:projectId page did.
 */
import * as moment from 'moment-timezone';
import { issueMediaFiles, IssueMediaLists } from './issue-media';
import { issueStatusLabel } from './issue-workflow';

export const ISSUES_TAB = 'issues';

/** Query params the Issues tab owns (cleared when another tab is picked). */
export const ISSUES_TAB_QUERY_PARAMS = ['category', 'scope', 'search', 'sort', 'sortDir', 'selectedId'] as const;

/**
 * `status=all`: the user cleared the status filter. Without it, no status
 * param means "apply the default" (the open statuses), so an empty selection
 * could not be told apart from a first visit.
 */
export const ISSUE_STATUS_ALL = 'all';

export interface IssuesTabOption {
  label: string;
  value: string;
  isChecked: boolean;
}

/**
 * Sort choices, in menu order: the table's sortable columns, so the Sort
 * dropdown and a click on a column header set the same thing.
 */
export const ISSUE_SORT_OPTIONS = ['Title', 'Site', 'Category', 'Status', 'Created at', 'Latest activity'] as const;
export type IssueSortOption = typeof ISSUE_SORT_OPTIONS[number];
export type IssueSortDir = 'asc' | 'desc';
export const DEFAULT_ISSUE_SORT: IssueSortOption = 'Created at';

/** The direction a column sorts in when picked: dates newest first, text A-Z. */
export function defaultIssueSortDir(sort: string | null | undefined): IssueSortDir {
  return sort === 'Created at' || sort === 'Latest activity' ? 'desc' : 'asc';
}

/** The plain statuses every project has. */
export const ISSUE_TAB_STATUSES: { value: string; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' },
];

export interface IssuesTabRow {
  id?: string | number;
  title?: string;
  description?: string;
  status?: string;
  type?: string;
  entity_type?: string;
  entity_id?: string | number;
  entity?: { id?: string | number; name?: string } | null;
  created_at?: unknown;
  updated_at?: unknown;
  closed_at?: unknown;
  createdBy?: IssuesTabPerson | null;
  updatedBy?: IssuesTabPerson | null;
  closedBy?: IssuesTabPerson | null;
}

export interface IssuesTabPerson {
  name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

/** The Latest activity cell: what happened, who did it and when. */
export interface IssueActivity {
  action: string;
  actor: string;
  date: unknown;
}

export interface IssuesTabFilters {
  statuses?: string[];
  categories?: string[];
  scopes?: string[];
  search?: string;
  startDate?: string | null;
  endDate?: string | null;
  /** IANA timezone the created_at day is read in (the project's); UTC when absent. */
  timezone?: string | null;
  /**
   * Maps an issue status onto the status values the filter offers. Identity by
   * default; a project without the contractor workflow can fold its stages
   * into open / closed here.
   */
  statusKeyOf?: (status: string | null | undefined) => string;
}

export function personName(person: IssuesTabPerson | null | undefined): string {
  if (!person) {
    return '';
  }
  return person.name || [person.first_name, person.last_name].filter(Boolean).join(' ') || person.email || '';
}

/**
 * Latest activity from what the issue list returns (it has no workflow
 * history): closed, else updated (a later edit by someone), else created.
 */
export function issueListActivity(issue: IssuesTabRow): IssueActivity {
  if (issue?.status === 'closed' && issue?.closed_at) {
    return { action: 'Closed', actor: personName(issue.closedBy), date: issue.closed_at };
  }
  const created = timeOf(issue?.created_at);
  const updated = timeOf(issue?.updated_at);
  // A save in the same minute as the create is the create itself.
  if (issue?.updatedBy && updated - created > 60 * 1000) {
    return { action: 'Updated', actor: personName(issue.updatedBy), date: issue.updated_at };
  }
  return { action: 'Created', actor: personName(issue?.createdBy), date: issue?.created_at };
}

/** Colour of a status pill: open (amber), closed (grey) or a workflow stage (pending amber). */
export function issueStatusTone(status: string | null | undefined): 'open' | 'closed' | 'pending' {
  if (!status || status === 'open') {
    return 'open';
  }
  return status === 'closed' ? 'closed' : 'pending';
}

/** Query param value(s) → string[] (Angular gives a string for one value). */
export function toParamList(value: unknown): string[] {
  if (value === undefined || value === null || value === '') {
    return [];
  }
  return (Array.isArray(value) ? value : [value]).map(v => String(v)).filter(Boolean);
}

/** The scope id of an issue: its site's id, or the project's for a project-level issue. */
export function issueScopeId(issue: IssuesTabRow, projectId?: string | null): string {
  const id = issue?.entity?.id ?? issue?.entity_id;
  if (id !== undefined && id !== null && id !== '') {
    return String(id);
  }
  return issue?.entity_type === 'project' && projectId ? projectId : '';
}

/** created_at as YYYY-MM-DD in the project's timezone, '' when missing / invalid. */
export function issueCreatedDay(issue: IssuesTabRow, timezone?: string | null): string {
  const raw: any = issue?.created_at;
  if (!raw) {
    return '';
  }
  const m = moment.isMoment(raw) ? raw.clone() : moment.utc(raw);
  if (!m.isValid()) {
    return '';
  }
  return m.tz(timezone || 'UTC').format('YYYY-MM-DD');
}

export function filterIssues<T extends IssuesTabRow>(
  issues: T[] | null | undefined,
  filters: IssuesTabFilters,
  projectId?: string | null,
): T[] {
  const statusKeyOf = filters.statusKeyOf || (status => status || '');
  const statuses = (filters.statuses || []).filter(s => s !== ISSUE_STATUS_ALL);
  const categories = filters.categories || [];
  const scopes = filters.scopes || [];
  const search = (filters.search || '').trim().toLowerCase();
  const start = filters.startDate && moment(filters.startDate, 'YYYY-MM-DD', true).isValid() ? filters.startDate : null;
  const end = filters.endDate && moment(filters.endDate, 'YYYY-MM-DD', true).isValid() ? filters.endDate : null;

  return (issues || []).filter(issue => {
    if (statuses.length && !statuses.includes(statusKeyOf(issue?.status))) {
      return false;
    }
    if (categories.length && !categories.includes(issue?.type)) {
      return false;
    }
    if (scopes.length && !scopes.includes(issueScopeId(issue, projectId))) {
      return false;
    }
    if (start || end) {
      const day = issueCreatedDay(issue, filters.timezone);
      if (!day || (start && day < start) || (end && day > end)) {
        return false;
      }
    }
    if (search) {
      const haystack = [issue?.title, issue?.description, issue?.entity?.name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) {
        return false;
      }
    }
    return true;
  });
}

function timeOf(value: unknown): number {
  if (!value) {
    return 0;
  }
  const m = moment.isMoment(value) ? value : moment.utc(value as any);
  return m.isValid() ? m.valueOf() : 0;
}

export interface IssueSortLabels<T> {
  status?: (s: string) => string;
  category?: (t: string) => string;
  /** When the issue's latest activity happened (Latest activity column). */
  activityTime?: (issue: T) => unknown;
}

/**
 * Sorts a copy of the list by a column. `labelOf` gives the text a category /
 * status is shown as, so those sort the way the user reads them. Ties fall
 * back to newest first.
 */
export function sortIssues<T extends IssuesTabRow>(
  issues: T[] | null | undefined,
  sort: string | null | undefined,
  dir: IssueSortDir = defaultIssueSortDir(sort),
  labelOf: IssueSortLabels<T> = {},
): T[] {
  const list = [...(issues || [])];
  const oldestFirst = (a: T, b: T) => timeOf(a?.created_at) - timeOf(b?.created_at);
  const text = (v: unknown) => String(v ?? '').toLowerCase();
  const statusText = (s: string) => text(labelOf.status ? labelOf.status(s) : s);
  const categoryText = (t: string) => text(labelOf.category ? labelOf.category(t) : t);
  const activity = (i: T) => timeOf(labelOf.activityTime ? labelOf.activityTime(i) : issueListActivity(i).date);

  const comparators: Record<string, (a: T, b: T) => number> = {
    'Title': (a, b) => text(a?.title).localeCompare(text(b?.title)),
    'Site': (a, b) => text(a?.entity?.name).localeCompare(text(b?.entity?.name)),
    'Category': (a, b) => categoryText(a?.type).localeCompare(categoryText(b?.type)),
    'Status': (a, b) => statusText(a?.status).localeCompare(statusText(b?.status)),
    'Created at': oldestFirst,
    'Latest activity': (a, b) => activity(a) - activity(b),
  };
  const fn = (sort && comparators[sort]) || null;
  if (!fn) {
    return list.sort((a, b) => -oldestFirst(a, b));
  }
  const sign = dir === 'desc' ? -1 : 1;

  return list.sort((a, b) => {
    const primary = fn(a, b) * sign;
    return primary !== 0 ? primary : -oldestFirst(a, b);
  });
}

/**
 * Tags an issue's pictures / videos / attachments with the `fileType` the
 * details panel's media section renders by (see issue-media) and sets
 * `media`, the combined list the daily report effects set too. Tags in
 * place and returns the issue.
 */
export function withIssueMediaTypes<T extends IssueMediaLists & { media?: unknown }>(issue: T): T {
  if (!issue) {
    return issue;
  }
  issue.media = issueMediaFiles(issue);
  return issue;
}

/** Checkbox options with the selected values ticked. */
export function toCheckboxOptions(
  items: { value: string; label: string }[],
  selected: string[],
): IssuesTabOption[] {
  return (items || []).map(item => ({
    label: item.label,
    value: item.value,
    isChecked: selected.includes(item.value),
  }));
}

/**
 * Where the old standalone page (/issues/project/:projectId) now lives: the
 * project's Issues tab. Keeps status, the open issue (selectedId) and the old
 * scope (?e=site&i=<id> / ?e=project&i=<id>) so bookmarks and emails land on
 * the same view.
 */
export function legacyIssuesRedirect(
  projectId: string,
  queryParams: Record<string, unknown> = {},
): { commands: string[]; queryParams: Record<string, string | string[]> } {
  const qp: Record<string, string | string[]> = { tab: ISSUES_TAB };
  const status = toParamList(queryParams.status);
  // status=open was the old "open issues" view: the tab opens on that by
  // default (every open status on a workflow project), so leave it to it.
  if (status.length && !(status.length === 1 && status[0] === 'open')) {
    qp.status = status;
  }
  const selectedId = toParamList(queryParams.selectedId)[0];
  if (selectedId) {
    qp.selectedId = selectedId;
  }
  const scopeId = toParamList(queryParams.i)[0];
  if (scopeId && (queryParams.e === 'site' || queryParams.e === 'project')) {
    qp.scope = scopeId;
  }
  return { commands: ['/projects', projectId], queryParams: qp };
}

// ---------------------------------------------------------------------------
// Export menu + project map: the tab's filters as a server query
// ---------------------------------------------------------------------------

/** The Issues tab's export menu: GET /v1/issue/export/<format>. */
export type IssueExportFormat = 'pdf' | 'csv' | 'kmz';

/** Rows of the export menu, in order: the punch list / claims kebab's labels and icons. */
export const ISSUE_EXPORT_OPTIONS: { format: IssueExportFormat; label: string; icon: string }[] = [
  { format: 'pdf', label: 'PDF Report', icon: 'picture_as_pdf' },
  { format: 'csv', label: 'CSV Export', icon: 'table_chart' },
  { format: 'kmz', label: 'KMZ Map File', icon: 'map' },
];

/** What the tab shows: the url's filters (see IssuesTabComponent) plus the sort. */
export interface IssuesTabQueryState {
  statuses?: string[];
  categories?: string[];
  scopes?: string[];
  startDate?: string | null;
  endDate?: string | null;
  search?: string | null;
  sort?: string | null;
  sortDir?: IssueSortDir | null;
  /** The project's IANA timezone: the date range is read in it, like filterIssues does. */
  timezone?: string | null;
}

/**
 * The tab's filter state read from the url, the way IssuesTabComponent reads
 * it. `statusSet` is false while the url has no status yet (the tab is about
 * to write the default there).
 */
export function issuesTabStateFromParams(qp: Record<string, unknown> = {}): IssuesTabQueryState & { statusSet: boolean } {
  const sort = qp.sort === undefined ? DEFAULT_ISSUE_SORT : ((qp.sort as string) || null);
  return {
    statusSet: qp.status !== undefined,
    statuses: toParamList(qp.status),
    categories: toParamList(qp.category),
    scopes: toParamList(qp.scope),
    startDate: (qp.startDate as string) || null,
    endDate: (qp.endDate as string) || null,
    search: (qp.search as string) || '',
    sort,
    sortDir: qp.sortDir === 'asc' || qp.sortDir === 'desc' ? qp.sortDir : defaultIssueSortDir(sort),
  };
}

/**
 * Query for the export and for-map endpoints (GET /v1/issue/export/:format,
 * GET /v1/issue/for-map): the same filters the list shows, no pagination.
 * Lists go comma-joined, like GET /v1/issue's `status`; `scope` holds site
 * ids and / or the project id (project-level issues); `sort_by` is the tab's
 * sort label. Empty filters and the cleared status (`all`) are left out. `projectId` is what GET /v1/issue
 * takes; `project_id` is sent too for the export / for-map contract.
 */
export function issuesTabApiQuery(projectId: string, state: IssuesTabQueryState): Record<string, string> {
  const qp: Record<string, string> = { projectId, project_id: projectId };
  const list = (values: string[] | undefined) => (values || []).filter(Boolean).join(',');
  const statuses = list((state.statuses || []).filter(s => s !== ISSUE_STATUS_ALL));
  const valid = (d: string | null | undefined) => !!d && moment(d, 'YYYY-MM-DD', true).isValid();
  if (statuses) qp.status = statuses;
  if (list(state.categories)) qp.type = list(state.categories);
  if (list(state.scopes)) qp.scope = list(state.scopes);
  if (valid(state.startDate)) qp.start_date = state.startDate;
  if (valid(state.endDate)) qp.end_date = state.endDate;
  if ((valid(state.startDate) || valid(state.endDate)) && state.timezone) qp.timezone = state.timezone;
  const search = (state.search || '').trim();
  if (search) qp.search = search;
  if (state.sort) {
    qp.sort_by = state.sort;
    qp.sort_order = state.sortDir || defaultIssueSortDir(state.sort);
  }
  return qp;
}

/** A row of GET /v1/issue/for-map. */
export interface IssueMapRow {
  id: string;
  title?: string;
  status?: string;
  internal?: boolean;
  latitude?: number | string | null;
  longitude?: number | string | null;
  entity_type?: string;
  entity_id?: string;
  site_name?: string;
  category?: string;
  category_label?: string;
  status_label?: string;
  /** Where the pin comes from: the issue's own position or a geotagged photo's. */
  location_source?: 'issue' | 'photo' | null;
}

/** GET /v1/issue/for-map `meta`: the matching issues, and those with no position at all. */
export interface IssueMapMeta {
  truncated?: boolean;
  total?: number;
  no_location_count?: number;
  no_location_ids?: string[];
}

/** The for-map pins and meta of one query (see issuesMapQuery). */
export interface IssueMapResult {
  projectId: string;
  /** JSON of the query the result is for: what the Issues tab compares its own filters with. */
  key: string;
  meta: IssueMapMeta;
}

/**
 * The for-map query of the Issues tab's url filters: the export query without
 * the sort. The map and the tab both build it from the url, so the tab can
 * tell whether the map's latest result is for what it shows.
 */
export function issuesMapQuery(
  projectId: string,
  qp: Record<string, unknown>,
  timezone: string | null | undefined,
): Record<string, string> {
  return issuesTabApiQuery(projectId, { ...issuesTabStateFromParams(qp), sort: null, timezone: timezone || null });
}

/** The bits of an issue the no-location card reads (a GET /v1/issue row). */
export interface IssueNoLocationSource {
  id?: string | number;
  title?: string;
  description?: string;
  status?: string;
  type?: string;
  entity_type?: string;
  entity?: { name?: string } | null;
  created_at?: unknown;
}

/** What map-popup-type-one renders for an Issues tab issue (the tab's row cells). */
export interface IssueCardFields {
  category: string;
  site: string;
  statusLabel: string;
  statusTone: 'open' | 'closed' | 'pending';
  /** created_at, as the tab's Created at column ("Oct 2, 2026") in the project's timezone. */
  date: string;
}

/**
 * One card of the map's no-location list for an Issues tab issue, in the
 * item popup the bid item / activity / delivery badge uses (map-popup-wrapper
 * → map-popup-type-one): the per-card "ISSUE" title like "BID ITEM", the
 * issue title, and `issueCard` (category chip, site, status chip + date as
 * the Issues tab shows them), the comment, "See more". Tagged `_kind: 'issue'`
 * so "See more" opens the issue (noLocationIssueOf).
 */
export function issueNoLocationPopup(
  issue: IssueNoLocationSource,
  opts: {
    categoryLabels?: Record<string, string>;
    timezone?: string | null;
  } = {},
): {
  type: 'issues';
  title: string;
  heading: string;
  issueCard: IssueCardFields;
  description: string;
  more_link_text: string;
  data: { type: 'issues'; markerData: { _kind: 'issue'; id: string | number } & Record<string, unknown> };
} {
  const site = issue?.entity_type === 'project' ? 'Project' : issue?.entity?.name || '';
  const status = issue?.status || 'open';
  const created: any = issue?.created_at;
  const m = created ? (moment.isMoment(created) ? created.clone() : moment.utc(created)) : null;
  return {
    type: 'issues',
    title: 'ISSUE',
    heading: issue?.title || '',
    issueCard: {
      category: (issue?.type && opts.categoryLabels?.[issue.type]) || issue?.type || '',
      site,
      statusLabel: issueStatusLabel(status),
      statusTone: issueStatusTone(status),
      date: m?.isValid() ? m.tz(opts.timezone || moment.tz.guess()).format('MMM D, YYYY') : '',
    },
    description: issue?.description || '',
    more_link_text: 'See more',
    data: { type: 'issues', markerData: { ...issue, _kind: 'issue', id: issue?.id } },
  };
}

/**
 * The issue behind a "See more" on the map's item list when it is one of the
 * Issues tab's no-location cards (see issueNoLocationPopup); null otherwise.
 */
export function noLocationIssueOf(event: { type?: string; data?: { markerData?: Record<string, unknown> } } | null | undefined):
  ({ id: string } & Record<string, unknown>) | null {
  const issue = event?.data?.markerData;
  if (event?.type !== 'issues' || issue?._kind !== 'issue' || !issue?.id) {
    return null;
  }
  return issue as { id: string } & Record<string, unknown>;
}

/** The no-location badge's tooltip: "1 issue has no location" / "N issues have no location"; '' for none. */
export function issueNoLocationLabel(count: number | null | undefined): string {
  const n = Number(count) || 0;
  if (n <= 0) {
    return '';
  }
  return n === 1 ? '1 issue has no location' : `${n} issues have no location`;
}

/** True when the issue is one of the for-map meta's no_location_ids (matched by id or uuid). */
export function issueHasNoLocation(
  issue: { id?: string | number; uuid?: string } | null | undefined,
  noLocationIds: ReadonlySet<string> | null | undefined,
): boolean {
  if (!issue || !noLocationIds?.size) {
    return false;
  }
  return (issue.uuid !== undefined && noLocationIds.has(String(issue.uuid)))
    || (issue.id !== undefined && issue.id !== null && noLocationIds.has(String(issue.id)));
}

/**
 * Map markers for the Issues tab, in the shape the punch list / claims tabs
 * push on mapService.warrantyIssueMarkers$ (the issue pin layer with
 * clustering). Rows without usable coordinates are left off. `_kind: 'issue'`
 * tells the map a click opens the Issues tab's panel.
 */
export function issueMapMarkers(rows: IssueMapRow[] | null | undefined, categoryLabel?: (type: string) => string): {
  geometry: { type: 'point'; latitude: number; longitude: number };
  symbol: 'warranty_issues';
  category: 'warranty_issues';
  data: Record<string, unknown>;
}[] {
  return (rows || [])
    .filter(row => row?.id && hasCoordinate(row.latitude) && hasCoordinate(row.longitude))
    .map(row => {
      const latitude = parseFloat(String(row.latitude));
      const longitude = parseFloat(String(row.longitude));
      return {
        geometry: { type: 'point' as const, latitude, longitude },
        symbol: 'warranty_issues' as const,
        category: 'warranty_issues' as const,
        data: {
          ...row,
          latitude,
          longitude,
          // The issue popup shows `type` as the category chip.
          type: row.category_label
            || (row.category ? (categoryLabel ? categoryLabel(row.category) : row.category) : ''),
          _kind: 'issue',
        },
      };
    });
}

function hasCoordinate(value: unknown): boolean {
  if (value === null || value === undefined || value === '') {
    return false;
  }
  const n = parseFloat(String(value));
  return Number.isFinite(n) && n !== 0;
}
